import { DestroyRef, inject, Injector } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, filter } from 'rxjs';
import { WebsocketService } from '../services/websocket.service';
import { MeasurementUpdatePayload, WebSocketMessageType } from '../types/websocket.types';

export const MEASUREMENT_FALLBACK_REFRESH_INTERVAL_MS = 30_000;

// A row may show the transient "Calcul en cours" badge at most this long without a terminal
// settle frame before the bridge force-settles — a stuck computing badge is unacceptable.
const PENDING_SETTLE_TIMEOUT_MS = 2_500;

interface WsUpdateRefreshOptions<T> {
  /** Returns true when the broadcast targets what this screen displays. */
  readonly targets: (payload: T) => boolean;
  /** Silent refresh — must be safe to call at any time. */
  readonly refresh: () => void;
  /** Coalesce a burst (e.g. a bulk recalc emitting one frame per measure) into a single
   *  refresh. Applied through the RxJS pipe so a trailing tick is torn down with the host. */
  readonly debounceMs?: number;
  /** Live-status hook. A 'pending' frame (a job just enqueued) is routed here immediately —
   *  bypassing debounce — to flag rows Pending, and the settle refresh() is skipped for it.
   *  Terminal frames, ces-calcul 'progress' partial-result ticks, and legacy frames all still
   *  refresh(). Omitting it keeps the legacy behaviour. */
  readonly onPending?: (payload: T) => void;
  /** Decides which frames are 'pending' enqueue ticks (vs settle). registerMeasurementUpdate-
   *  Refresh defaults this to calculationStatus === 'pending' when onPending is wired. */
  readonly isPending?: (payload: T) => boolean;
  /** Override the force-settle delay after a pending tick with no terminal frame. */
  readonly pendingSettleTimeoutMs?: number;
}

interface WsUpdateRefreshHandle {
  /** Starts the fallback poll (idempotent); ticks only refresh while the socket is down. */
  readonly startFallbackPoll: () => void;
  /** Opt-in: refresh once when the socket recovers from a drop, so an update emitted during
   *  the outage is reconciled. Skips the initial connection — the host already loaded on init. */
  readonly startReconnectCatchup: () => void;
}

/** Wires a websocket-driven silent refresh for one message type, plus opt-in resilience
 *  (a poll that covers the gap while the socket is down, and a catch-up on reconnect). Must be
 *  called from an injection context — every subscription is torn down with the host. */
export function registerWsUpdateRefresh<T>(type: WebSocketMessageType, options: WsUpdateRefreshOptions<T>): WsUpdateRefreshHandle {
  const ws = inject(WebsocketService);
  const destroyRef = inject(DestroyRef);
  const injector = inject(Injector);
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const matched = ws.getObservable<T>(type).pipe(filter(options.targets));

  let settleTimerId: ReturnType<typeof setTimeout> | null = null;
  const clearSettleTimer = (): void => {
    if (settleTimerId) {
      clearTimeout(settleTimerId);
      settleTimerId = null;
    }
  };
  const settle = (): void => {
    clearSettleTimer();
    options.refresh();
  };

  const onPending = options.onPending;
  if (onPending) {
    // 'pending' ticks flag rows Pending at once (no debounce) so "Calcul en cours" shows before
    // the terminal frame settles; a lost terminal frame can never strand the badge — the
    // force-settle timer refetches after pendingSettleTimeoutMs.
    const isPending = options.isPending ?? ((): boolean => false);
    matched.pipe(takeUntilDestroyed(destroyRef)).subscribe(payload => {
      if (isPending(payload)) {
        onPending(payload);
        clearSettleTimer();
        settleTimerId = setTimeout(settle, options.pendingSettleTimeoutMs ?? PENDING_SETTLE_TIMEOUT_MS);
      } else {
        settle();
      }
    });
  } else {
    const stream = options.debounceMs ? matched.pipe(debounceTime(options.debounceMs)) : matched;
    stream.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => options.refresh());
  }

  destroyRef.onDestroy(() => {
    clearSettleTimer();
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  });

  return {
    startFallbackPoll: () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        if (!ws.isConnected()) {
          options.refresh();
        }
      }, MEASUREMENT_FALLBACK_REFRESH_INTERVAL_MS);
    },
    startReconnectCatchup: () => {
      let wasUp = ws.isConnected();
      // If already connected at registration the initial connect has passed; otherwise the
      // first false->true IS that initial connect and must be skipped (the host loads on init).
      let initialConnectSeen = ws.isConnected();
      // toObservable is created lazily here (with the captured injector) so a subscriber that
      // never opts into catch-up pays no effect cost.
      toObservable(ws.connected, { injector })
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(up => {
          if (up && !wasUp) {
            if (initialConnectSeen) {
              options.refresh();
            } else {
              initialConnectSeen = true;
            }
          }
          wasUp = up;
        });
    },
  };
}

/** Backward-compatible MEASUREMENT_UPDATE wrapper — the established subscribers (campaign
 *  measures, analysis-post detail, automatic-measure detail) keep their exact behaviour. */
export function registerMeasurementUpdateRefresh(options: WsUpdateRefreshOptions<MeasurementUpdatePayload>): WsUpdateRefreshHandle {
  // Default the pending predicate so callers only wire onPending; conditional spread keeps
  // exactOptionalPropertyTypes happy (never assigns isPending: undefined). ces-calcul's own
  // 'progress' partial-result frames deliberately fall through to refresh(), not onPending.
  return registerWsUpdateRefresh(WebSocketMessageType.MEASUREMENT_UPDATE, {
    ...options,
    ...(options.onPending && !options.isPending
      ? { isPending: (p: MeasurementUpdatePayload): boolean => p.calculationStatus === 'pending' }
      : {}),
  });
}
